import * as React from 'react';
import {FunctionComponent, useEffect, useMemo, useState} from 'react';
import {DownloadedIndoorWorkout} from '../Models/DownloadedIndoorWorkout';
import {classNames} from '../Ui/Support/classNames';
import {PowerGraph} from './PowerGraph';
import {SplitGraph} from './SplitGraph';
import {PowerSplitGraph} from './PowerSplitGraph';
import {StrokeRateGraph} from './StrokeRateGraph';
import {StrokeLengthGraph} from './StrokeLengthGraph';
import {CurveMetricsGraph} from './CurveMetricsGraph';
import {HeartrateGraph} from './HeartrateGraph';
import {DualRange} from '../Ui/Components/Form/DualRange';
import {Stroke} from '../Models/Stroke';
import {FeatureFlags} from '../FeatureFlags';
import {getXAxisUnitFromKey, getXAxisUnitLabel, XAxisUnit} from './support/variableXAxis';
import {downloadedIndoorWorkoutToStrokes} from '../Strokes/downloadedIndoorWorkoutToStrokes';
import {RangeInput} from '../Models/RangeInput';
import {RelativePeakForcePositionGraph} from './RelativePeakForcePositionGraph';
import Dropdown from 'react-bootstrap/Dropdown';
import {useLocalStorage} from 'usehooks-ts';
import {PowerMode} from '../Ui/Settings/settings';

interface OwnProps {
    downloadedIndoorWorkout: DownloadedIndoorWorkout;
    range?: RangeInput | null;
    onRangeChange?: (strokeRange: RangeInput | null) => void;
    className?: string;
    powerMode?: PowerMode;
}

const initialVisibilityState = {
    split: true,
    power: true,
    powerSplit: false,
    heartrate: true,
    strokerate: true,
    strokeLength: false,
    relativePeakForcePosition: false,
    curveMetrics: false,
};

const visibilityItems = [
    {key: 'split', label: 'Split'},
    {key: 'power', label: 'Power'},
    FeatureFlags.powerSplit ? {key: 'powerSplit', label: 'Split - Power'} : {},
    {key: 'heartrate', label: 'Heartrate'},
    {key: 'strokerate', label: 'Strokerate'},
    {key: 'strokeLength', label: 'Stroke Length'},
    {key: 'relativePeakForcePosition', label: 'Relative Peak Force Position'},
    {key: 'curveMetrics', label: 'Curve R²'},
].filter(Boolean);

export const Graphs: FunctionComponent<OwnProps> = ({
    downloadedIndoorWorkout,
    range,
    onRangeChange,
    className,
    powerMode,
}) => {
    const strokeCount = downloadedIndoorWorkoutToStrokes(downloadedIndoorWorkout).length;
    const [strokeIndexRange, setStrokeIndexRange] = useState<{min: number; max: number}>({min: 1, max: strokeCount});
    const [activeData, setActiveData] = useState<{point: {x: number; y: number; label?: string}; x: number} | null>(
        null,
    );
    const [visibility, setVisibility] = useLocalStorage('preferences.indoor-graphs.visibility', initialVisibilityState);

    const handleToggle = (key: keyof typeof initialVisibilityState) => {
        setVisibility((prev) => ({
            ...prev,
            [key]: !prev[key],
        }));
    };

    useEffect(() => {
        if (range !== undefined) {
            if (range === null) {
                setStrokeIndexRange({min: 1, max: strokeCount});
            } else {
                const strokes = downloadedIndoorWorkoutToStrokes(downloadedIndoorWorkout);

                setStrokeIndexRange({
                    min:
                        strokes.findIndex((stroke: Stroke) => {
                            return stroke.totalTime.gte(range.start);
                        }) + 1,
                    max:
                        strokes.findLastIndex((stroke: Stroke) => {
                            return stroke.totalTime.lte(range.end);
                        }) + 1,
                });
            }
        }
    }, [range]);

    const allStrokes = useMemo(
        () => downloadedIndoorWorkoutToStrokes(downloadedIndoorWorkout),
        [downloadedIndoorWorkout, strokeIndexRange],
    );

    const strokes = allStrokes.slice(strokeIndexRange.min - 1, strokeIndexRange.max);

    const [xAxisUnit, setXAxisUnit] = useState<XAxisUnit>(XAxisUnit.Strokes);

    return (
        <div className={classNames('card', className)} onMouseLeave={() => setActiveData(null)} id="graphs">
            <div className="card-title">
                <h5 className="d-flex flex-row">
                    <span className="p-2 me-auto">Graphs</span>
                    {FeatureFlags.graphSelection && (
                        <Dropdown className="dropdown p-2" autoClose="outside">
                            <Dropdown.Toggle
                                className="btn btn-secondary btn-sm dropdown-toggle"
                                type="button"
                                id="dropdown-autoclose-outside"
                                data-bs-toggle="dropdown"
                                aria-expanded="false"
                            >
                                Graphs
                            </Dropdown.Toggle>
                            <Dropdown.Menu aria-labelledby="visibilityDropdown">
                                {visibilityItems.map((item) => (
                                    <li key={item.key}>
                                        <label className="dropdown-item">
                                            <input
                                                type="checkbox"
                                                checked={visibility[item.key as keyof typeof visibility]}
                                                onChange={() => handleToggle(item.key as keyof typeof visibility)}
                                            />
                                            <span className="ms-1">{item.label}</span>
                                        </label>
                                    </li>
                                ))}
                            </Dropdown.Menu>
                        </Dropdown>
                    )}
                    {FeatureFlags.graphVariableXAxis && (
                        <div className="dropdown p-2">
                            <button
                                className="btn btn-secondary btn-sm dropdown-toggle"
                                type="button"
                                id="splittablemode"
                                data-bs-toggle="dropdown"
                                aria-expanded="false"
                            >
                                {getXAxisUnitLabel(xAxisUnit)}
                            </button>
                            <ul className="dropdown-menu" aria-labelledby="splittablemode">
                                {Object.keys(XAxisUnit)
                                    .filter((key) => !isNaN(parseInt(key, 0))) // It would not matter if you use the numerical or string keys, just not both. Using both would cause every option to be in the list twice.
                                    .map((key) => getXAxisUnitFromKey(key))
                                    .map((xAxisOption: XAxisUnit, xAxisOptionIndex) => {
                                        return (
                                            <li key={'x-axis-option-' + xAxisOptionIndex}>
                                                <button
                                                    className="dropdown-item"
                                                    onClick={() => {
                                                        setXAxisUnit(xAxisOption);
                                                    }}
                                                >
                                                    {getXAxisUnitLabel(xAxisOption)}
                                                </button>
                                            </li>
                                        );
                                    })}
                            </ul>
                        </div>
                    )}
                </h5>
            </div>
            <div className="card-body graphs">
                {Object.values(visibility).every((value) => value === false) && (
                    <div className="row">
                        <div className="col-12">
                            <p>No graph is selected. Please select a graph above by clicking the "Graphs" button.</p>
                        </div>
                    </div>
                )}
                {visibility.split && (
                    <div className="row">
                        <div className="col-12">
                            <p className="graphs__label">Split (/500m)</p>
                            <SplitGraph
                                tooltip={activeData}
                                xAxisUnit={xAxisUnit}
                                onDataActivated={setActiveData}
                                strokes={strokes}
                            />
                        </div>
                    </div>
                )}
                {visibility.power && (
                    <div className="row">
                        <div className="col-12">
                            <p className="graphs__label">Power (W)</p>
                            <PowerGraph
                                tooltip={activeData}
                                xAxisUnit={xAxisUnit}
                                onDataActivated={setActiveData}
                                strokes={strokes}
                            />
                        </div>
                    </div>
                )}
                {FeatureFlags.powerSplit && (
                    <div className="row">
                        <div className="col-12">
                            {visibility.powerSplit && <p className="graphs__label">Split Power (W)</p>}
                            <PowerSplitGraph
                                tooltip={activeData}
                                xAxisUnit={xAxisUnit}
                                onDataActivated={setActiveData}
                                strokes={strokes}
                                show={visibility.powerSplit}
                            />
                        </div>
                    </div>
                )}
                {strokes.some((stroke) => stroke.heartRate !== null) && visibility.heartrate && (
                    <div className="row">
                        <div className="col-12">
                            <p className="graphs__label">Heartrate (bpm)</p>
                            <HeartrateGraph
                                tooltip={activeData}
                                xAxisUnit={xAxisUnit}
                                onDataActivated={setActiveData}
                                strokes={strokes}
                            />
                        </div>
                    </div>
                )}
                {visibility.strokerate && (
                    <div className="row">
                        <div className="col-12">
                            <p className="graphs__label">Stroke rate (/min)</p>
                            <StrokeRateGraph
                                tooltip={activeData}
                                xAxisUnit={xAxisUnit}
                                onDataActivated={setActiveData}
                                strokes={strokes}
                            />
                        </div>
                    </div>
                )}
                {visibility.strokeLength && (
                    <div className="row">
                        <div className="col-12">
                            <p className="graphs__label">Stroke Length (M)</p>
                            <StrokeLengthGraph
                                tooltip={activeData}
                                xAxisUnit={xAxisUnit}
                                onDataActivated={setActiveData}
                                strokes={strokes}
                            />
                        </div>
                    </div>
                )}
                {visibility.relativePeakForcePosition && (
                    <div className="row">
                        <div className="col-12">
                            <p className="graphs__label">Relative Peak Force Position (%)</p>
                            <RelativePeakForcePositionGraph
                                tooltip={activeData}
                                xAxisUnit={xAxisUnit}
                                onDataActivated={setActiveData}
                                strokes={strokes}
                            />
                        </div>
                    </div>
                )}
                {FeatureFlags.extendedStrokeAnalyzer && (
                    <div className="row">
                        <div className="col-12">
                            {visibility.curveMetrics && <p className="graphs__label">Curve R²(%)</p>}
                            <CurveMetricsGraph
                                tooltip={activeData}
                                xAxisUnit={xAxisUnit}
                                onDataActivated={setActiveData}
                                strokes={strokes}
                                show={visibility.curveMetrics}
                            />
                        </div>
                    </div>
                )}
                {FeatureFlags.graphRangeSelection && onRangeChange && (
                    <div className="row">
                        <div className="col-12">
                            <DualRange
                                value={strokeIndexRange}
                                onChange={(r) => {
                                    setActiveData(null);
                                    setStrokeIndexRange(r);

                                    if (onRangeChange !== undefined) {
                                        onRangeChange({
                                            start: allStrokes[r.min - 1].totalTime,
                                            end: allStrokes[r.max - 1].totalTime,
                                            predetermined: {},
                                        });
                                    }
                                }}
                                min={1}
                                max={strokeCount}
                                step={1}
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
